import { has, values, isEmpty, cloneDeep, keys, head, isNil } from 'lodash';
// Import Store
import { setDynamicSegmentDefaultFilterList } from 'store/slices/dynamicSegment';
// Import Helpers
import { getExclusionRulesQueryConfigurations, getQueryConfigurations } from 'services/Helpers';
// Import Services
import { ExclusionRulesHttpService, SegmentHttpService } from 'services/http';
//Import Constants
import { l } from 'constants/common';
// Import Enums
import { ObjectDefinitionTypeEnum } from 'enums';

const createResultDataForSubSegments = (subSegmentFilters) => {
    return subSegmentFilters.map(({ filters, id, name }) => {
        return {
            Id: id,
            Name: name,
            QueryConfiguration: {
                Columns: null,
                Filters: [filters],
                Table: { Id: 4 },
            },
        };
    });
};

const createUIModelDataFromQueryConfigurations = (queryConfigurations, data, objectId, t) => {
    let resultFilterList = {};
    const mainSegmentFilterList = head(data.Filters);
    const subSegmentsFilterList = data?.SubSegments;
    const mainResultFilterList = createFilterListFromQueryConfigurations(queryConfigurations, mainSegmentFilterList);
    resultFilterList[objectId] = {
        filters: mainResultFilterList,
        id: objectId,
        isMain: true,
        seqId: 0,
        name: t(l.MainSegment),
    };

    const subSegmentsResultFilterList = isNil(subSegmentsFilterList)
        ? {}
        : subSegmentsFilterList.reduce((acc, val, index) => {
              acc[val.Id] = {
                  seqId: index + 1,
                  id: val.Id,
                  name: val.Name,
                  filters: val.QueryConfiguration.Filters.reduce((acc, val) => {
                      acc = createFilterListFromQueryConfigurations(queryConfigurations, val);
                      return acc;
                  }, {}),
              };
              return acc;
          }, {});

    resultFilterList = { ...resultFilterList, ...subSegmentsResultFilterList };

    return resultFilterList;
};

const createFilterListFromQueryConfigurations = (queryConfigurations, filterData) => {
    const resultFilterList: AnyObject = {};
    filterData.forEach((filter) => {
        const { Column, Comparision, Value } = filter;
        const comparison: Nilable<AnyObject> = cloneDeep(queryConfigurations[`comparison:${Column.Id}/${Comparision}`]);
        comparison?.setValue(Value);
        if (has(resultFilterList, comparison?.parent.hashKey)) {
            resultFilterList[comparison?.parent.hashKey] = [
                ...resultFilterList[comparison?.parent.hashKey],
                comparison,
            ];
        } else {
            comparison?.setAdditionalData(null);
            resultFilterList[comparison?.parent.hashKey] = [comparison];
        }
    });
    return resultFilterList;
};

const createResultDataForDynamicSegment = (
    Name,
    Description,
    filterList,
    segmentId,
    definitionType,
    topListOption,
    predefinedOptions,
) => {
    const isPredefined = definitionType === ObjectDefinitionTypeEnum.Predefined;
    const mainSegmentFilters = filterList.find((el) => el.isMain);
    const subSegmentFilters = filterList.filter((el) => !el.isMain);
    const resultData: AnyObject = {
        Name,
        Description,
        DefinitionType: definitionType,
        ...(isPredefined && {
            PredefinedCategories: predefinedOptions.categories,
            PredefinedRegions: predefinedOptions.regions,
        }),
        QueryConfiguration: {
            Columns: null,
            Filters: [mainSegmentFilters?.filters],
            Table: { Id: 4 },
            ...(topListOption.isOpenTopList && {
                Pageing: { PageSize: topListOption.topClientsCount, PageNumber: 1 },
                Sorting: { Id: topListOption.kpi, Direction: topListOption.order },
            }),
        },
    };
    if (segmentId) {
        resultData.SegmentId = +segmentId;
    }
    if (!isEmpty(subSegmentFilters)) {
        resultData.QueryConfiguration.SubSegments = createResultDataForSubSegments(subSegmentFilters);
    }
    return resultData;
};

const createResultDataForExclusionRules = (Name, Description, filterList, exclusionRuleId) => {
    // Actually here the filterList contains only one elem in array (the Main itself), just for future I left the model like in dynamicSegment
    const mainSegmentFilters = filterList.find((el) => el.isMain);

    const resultData: AnyObject = {
        Name,
        Description,
        QueryConfiguration: {
            Columns: null,
            Filters: [mainSegmentFilters?.filters],
            Table: { Id: 4 },
        },
    };

    if (exclusionRuleId) {
        resultData.exclusionRuleId = +exclusionRuleId;
    }

    return resultData;
};

const createQueryConfigurationsFromFilters = (filterList, Columns = null, unifiedCurrency = undefined) => {
    const filters = createResultFilterListForDynamicSegment(filterList);
    const mainSegmentFilters = filters.find((el) => el.isMain);
    const subSegmentFilters = filters.filter((el) => !el.isMain);

    const resultData: AnyObject = {
        Columns,
        Table: { Id: 4 },
        Filters: [mainSegmentFilters?.filters],
        UnifiedCurrency: unifiedCurrency,
    };
    if (!isEmpty(subSegmentFilters)) {
        resultData.SubSegments = createResultDataForSubSegments(subSegmentFilters);
    }

    return resultData;
};

const createResultFilterListForDynamicSegment = (filterList) => {
    return keys(filterList).map((id) => {
        const filters: AnyObject[] = [];
        values(filterList[id]?.filters).forEach((filterGroup) => {
            filterGroup?.forEach((filterItem) => {
                if (isNil(filterItem)) {
                    return;
                }
                const { comparison, Value } = filterItem;
                filters.push({
                    Column: { Id: comparison?.columnId },
                    Comparision: comparison?.comparison,
                    Value,
                });
            });
        });
        return {
            filters: filters,
            id,
            name: filterList[id]?.name,
            seqId: filterList[id]?.seqId,
            isMain: filterList[id]?.isMain ?? false,
        };
    });
};

const createQueryConfigurationHandler = ({ httpServiceMethod, fetchHandler }) => {
    const requestInstance = httpServiceMethod();

    return {
        request: (dispatch, queryConfigurations, objectInfo, action, t) => {
            if (isEmpty(queryConfigurations)) {
                fetchHandler(dispatch, requestInstance.request)
                    .then((res) => {
                        setFilterList(dispatch, res, objectInfo, action, t);
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.log(error);
                    });
            } else {
                setFilterList(dispatch, queryConfigurations, objectInfo, action, t);
            }
        },
        cancel: requestInstance.cancel,
    };
};

const getQueryConfigurationsList = () =>
    createQueryConfigurationHandler({
        httpServiceMethod: SegmentHttpService.getQueryConfigurationsList,
        fetchHandler: getQueryConfigurations,
    });

const getExclusionRulesQueryConfigurationsWithFiltersSet = () =>
    createQueryConfigurationHandler({
        httpServiceMethod: ExclusionRulesHttpService.getExclusionRulesSegmentationCategories,
        fetchHandler: getExclusionRulesQueryConfigurations,
    });

function setFilterList(dispatch, queryConf, objectInfo, action, t) {
    const objectId = objectInfo.SegmentId ?? objectInfo.ReportId ?? objectInfo.ExclusionRuleId;
    const data = createUIModelDataFromQueryConfigurations(queryConf, objectInfo.QueryConfiguration, objectId, t);

    if (action?.shouldNotDispatch) {
        action.setData(data);
        return;
    }

    const dispatchAction = action || setDynamicSegmentDefaultFilterList;
    dispatch(dispatchAction(data));
}

export {
    createFilterListFromQueryConfigurations,
    createResultDataForDynamicSegment,
    createResultFilterListForDynamicSegment,
    createQueryConfigurationsFromFilters,
    getQueryConfigurationsList,
    getExclusionRulesQueryConfigurationsWithFiltersSet,
    createUIModelDataFromQueryConfigurations,
    createResultDataForExclusionRules,
};
